import React, { useEffect, useRef, useState } from 'react';
import { Search, Pencil, Wrench, Rocket, HeadphonesIcon } from 'lucide-react';
import './ProcessFlow.css';

const processSteps = [
  {
    icon: Search,
    title: 'Discover',
    subtitle: 'Understanding Your Vision',
    description: 'We begin by deeply understanding your business goals, challenges, and requirements through detailed consultations and research.',
    keyPoints: ['Requirements Analysis', 'Market Research', 'Goal Definition', 'Stakeholder Interviews'],
  },
  {
    icon: Pencil,
    title: 'Design',
    subtitle: 'Crafting the Blueprint',
    description: 'Our team creates comprehensive design solutions, wireframes, and technical architecture tailored to your specific needs.',
    keyPoints: ['UI/UX Design', 'System Architecture', 'Prototyping', 'Technical Planning'],
  },
  {
    icon: Wrench,
    title: 'Develop',
    subtitle: 'Building with Excellence',
    description: 'Expert developers bring designs to life using cutting-edge technologies, best practices, and agile methodologies.',
    keyPoints: ['Agile Development', 'Code Quality', 'Regular Updates', 'Testing & QA'],
  },
  {
    icon: Rocket,
    title: 'Deploy',
    subtitle: 'Launch with Confidence',
    description: 'Seamless deployment to production with comprehensive testing, monitoring, and a smooth transition to your live environment.',
    keyPoints: ['Production Setup', 'Performance Testing', 'Security Audit', 'Go-Live Support'],
  },
  {
    icon: HeadphonesIcon,
    title: 'Support',
    subtitle: 'Continuous Partnership',
    description: 'Ongoing maintenance, optimization, and support to ensure your solution continues to deliver value and stays ahead.',
    keyPoints: ['24/7 Monitoring', 'Regular Updates', 'Performance Optimization', 'Technical Support'],
  },
];

export function ProcessFlow() {
  const [activeStep, setActiveStep] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isVisible || isPaused) return;

    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % processSteps.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isVisible, isPaused]);

  const handleStepClick = (index: number) => {
    setActiveStep(index);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 8000);
  };

  return (
    <section className="process-flow section" ref={sectionRef}>
      <div className="process-header">
        <h2 className="section-title">Our Proven Process</h2>
        <p className="section-subtitle">
          A structured, transparent approach that transforms ideas into successful digital solutions.
        </p>
      </div>

      <div className="process-layout">
        {/* Left side - Step indicators */}
        <div className="process-steps-list">
          {processSteps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div
                key={index}
                className={`step-indicator ${index === activeStep ? 'active' : ''} ${
                  index < activeStep ? 'completed' : ''
                }`}
                onClick={() => handleStepClick(index)}
              >
                <div className="step-number-wrapper">
                  <div className="step-number">{index + 1}</div>
                  <div className="step-icon-small">
                    <Icon size={20} />
                  </div>
                </div>
                <div className="step-info">
                  <h4 className="step-indicator-title">{step.title}</h4>
                  <p className="step-indicator-subtitle">{step.subtitle}</p>
                </div>
                <div className="step-connector"></div>
              </div>
            );
          })}
        </div>

        {/* Center - Circular visualization */}
        <div className="process-circle-container">
          <svg className="process-circle" viewBox="0 0 300 300">
            <defs>
              <linearGradient id="circleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style={{ stopColor: '#ff6b35', stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: '#f7931e', stopOpacity: 1 }} />
              </linearGradient>
              
              <filter id="glow">
                <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            
            {/* Background circle */}
            <circle
              cx="150"
              cy="150"
              r="120"
              fill="none"
              stroke="rgba(255, 107, 53, 0.1)"
              strokeWidth="2"
            />
            
            {/* Animated progress circle */}
            <circle
              cx="150"
              cy="150"
              r="120"
              fill="none"
              stroke="url(#circleGradient)"
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={`${(2 * Math.PI * 120 * (activeStep + 1)) / processSteps.length} ${2 * Math.PI * 120}`}
              transform="rotate(-90 150 150)"
              filter="url(#glow)"
              style={{
                transition: 'stroke-dasharray 1s ease',
              }}
            />

            {/* Center icon */}
            <circle
              cx="150"
              cy="150"
              r="50"
              fill="url(#circleGradient)"
              opacity="0.1"
            />

            {/* Process step dots */}
            {processSteps.map((step, index) => {
              const angle = (index / processSteps.length) * 2 * Math.PI - Math.PI / 2;
              const x = 150 + 120 * Math.cos(angle);
              const y = 150 + 120 * Math.sin(angle);
              
              return (
                <g key={index} style={{ cursor: 'pointer' }} onClick={() => handleStepClick(index)}>
                  <circle
                    cx={x}
                    cy={y}
                    r="10"
                    fill={index <= activeStep ? 'url(#circleGradient)' : 'white'}
                    stroke={index <= activeStep ? 'none' : 'rgba(255, 107, 53, 0.3)'}
                    strokeWidth="2"
                    style={{
                      transition: 'all 0.5s ease',
                    }}
                  />
                  {index === activeStep && (
                    <>
                      <circle
                        cx={x}
                        cy={y}
                        r="15"
                        fill="none"
                        stroke="url(#circleGradient)"
                        strokeWidth="2"
                        opacity="0.5"
                      >
                        <animate
                          attributeName="r"
                          from="15"
                          to="25"
                          dur="2s"
                          repeatCount="indefinite"
                        />
                        <animate
                          attributeName="opacity"
                          from="0.5"
                          to="0"
                          dur="2s"
                          repeatCount="indefinite"
                        />
                      </circle>
                    </>
                  )}
                </g>
              );
            })}
          </svg>

          <div className="circle-center-text">
            <div className="center-step-number">Step {activeStep + 1}</div>
            <div className="center-step-title">{processSteps[activeStep].title}</div>
          </div>
        </div>

        {/* Right side - Detailed information */}
        <div className="process-details">
          <div className="detail-card" key={activeStep}>
            <div className="detail-icon-wrapper">
              {React.createElement(processSteps[activeStep].icon, { size: 40 })}
            </div>
            <h3 className="detail-title">{processSteps[activeStep].title}</h3>
            <h4 className="detail-subtitle">{processSteps[activeStep].subtitle}</h4>
            <p className="detail-description">{processSteps[activeStep].description}</p>
            
            <div className="detail-keypoints">
              <h5 className="keypoints-title">Key Activities:</h5>
              <ul className="keypoints-list">
                {processSteps[activeStep].keyPoints.map((point, idx) => (
                  <li key={idx} style={{ animationDelay: `${idx * 0.1}s` }}>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="navigation-dots">
            {processSteps.map((_, index) => (
              <button
                key={index}
                className={`nav-dot ${index === activeStep ? 'active' : ''}`}
                onClick={() => handleStepClick(index)}
                aria-label={`Go to step ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
